import React, { useState, useEffect } from "react";
import { Modal, Tag, Empty, Collapse, message, List, Pagination } from "antd";
import { getDirectusClient } from "../../../lib/directus";
import { readItems } from "@directus/sdk";
import { formatPrice } from "../../../lib/utils";
import { useSelector } from "react-redux";

const { Panel } = Collapse;

function LoanDetailsModal({ visible, onCancel, selectedLoan, serialesCounts, tipo = "normal" }) {

  const [productosAgrupados, setProductosAgrupados] = useState([]);
  const [paginacionProductos, setPaginacionProductos] = useState({});
  const [productosNoSerializados, setProductosNoSerializados] = useState([]);
  const signoMoneda = useSelector((state) => state.signoMoneda.signo_moneda);

  const fetchSerialDetails = async (loan) => {
    try {
      const directus = await getDirectusClient();

      // Determinar el filtro según el tipo
      let filter = {};
      if (tipo === "tropas") {
        filter = {
          prestamos_tropas: {
            prestamos_tropas_id: { _eq: loan.id },
          },
        };
      } else {
        filter = {
          prestamo: {
            prestamos_id: { _eq: loan.id },
          },
        };
      }

      const response = await directus.request(
        readItems("seriales", {
          fields: [
            "id",
            "estado",
            "caja",
            "serial",
            "user",
            "producto",
            "bodega",
            "producto.nombre",
            "producto.preciosinimp",
            "producto.id",
            "producto.grupo.impuesto",
          ],
          filter: filter,
          limit: -1, // Traer todos los seriales
        })
      );

      // Agrupar seriales por producto
      const agrupados = response.reduce((acc, serial) => {
        const productoId = serial.producto?.id;
        if (!acc[productoId]) {
          acc[productoId] = {
            producto: serial.producto,
            seriales: [],
            cantidad: 0,
          };
        }
        acc[productoId].seriales.push(serial);
        acc[productoId].cantidad += 1;
        return acc;
      }, {});

      const productosArray = Object.values(agrupados);
      setProductosAgrupados(productosArray);

      // Inicializar paginación para cada producto
      const paginacionInicial = {};
      productosArray.forEach((producto) => {
        paginacionInicial[producto.producto.id] = {
          current: 1,
          pageSize: 10,
          total: producto.seriales.length,
        };
      });
      setPaginacionProductos(paginacionInicial);
    } catch (error) {
      console.error("Error fetching serial details:", error);
      message.error("Error al cargar los detalles de los seriales");
    }
  };

  const fetchNonSerializedProducts = async (loan) => {
    try {
      const directus = await getDirectusClient();
      
      const response = await directus.request(
        readItems("inventario_ns_prestamo", {
          fields: [
            "id",
            "cantidad",
            "estado",
            "inventario_ns.nombre",
            "inventario_ns.producto.nombre",
            "inventario_ns.producto.preciosinimp",
            "inventario_ns.producto.grupo.impuesto",
          ],
          filter: {
            prestamos: {
              prestamos_id: { _eq: loan.id },
            },
          },
          limit: -1,
        })
      );

      setProductosNoSerializados(response);
    } catch (error) {
      console.error("Error fetching non-serialized products:", error);
      message.error("Error al cargar los productos no serializados");
    }
  };

  useEffect(() => {
    if (visible && selectedLoan) {
      fetchSerialDetails(selectedLoan);
      fetchNonSerializedProducts(selectedLoan);
    }
  }, [visible, selectedLoan]);

  const getEstadoText = (estado) => {
    switch (estado) {
      case 8:
        return "Solicitud de préstamo";
      case 1:
        return "Activo";
      case 2:
        return "Facturado";
      case 3:
        return "Prestado";
      case 9:
        return "Tropas";
      default:
        return "Otro";
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 8:
        return "blue";
      case 1:
        return "green";
      case 2:
        return "yellow";
      case 3:
        return "purple";
        case 9:
          return "cyan";
      default:
        return "gold";
    }
  };

  const handlePaginacionChange = (productoId, page, pageSize) => {
    setPaginacionProductos((prev) => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        current: page,
        pageSize: pageSize,
      },
    }));
  };

  const getSerialesPaginados = (producto) => {
    const paginacion = paginacionProductos[producto.producto?.id];
    if (!paginacion) return producto.seriales;

    const inicio = (paginacion.current - 1) * paginacion.pageSize;
    const fin = inicio + paginacion.pageSize;
    return producto.seriales.slice(inicio, fin);
  };
console.log("loan", selectedLoan);

  return (
    <Modal
      title={`Detalles del Préstamo - ${selectedLoan?.vendedor?.first_name} `}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <div style={{ marginBottom: 16 }}>
        <h4>Productos Serializados ({productosAgrupados.length})</h4>
      </div>

      <Collapse defaultActiveKey={[]} ghost>
        {productosAgrupados.map((producto, index) => {
          const paginacion = paginacionProductos[producto.producto?.id];
          const serialesPaginados = getSerialesPaginados(producto);

          return (
            <Panel
              key={`producto-${index}`}
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <span>{producto.producto?.nombre}</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tag color="green">{producto?.cantidad} seriales</Tag>
                    <Tag color={getEstadoColor(producto?.seriales[0]?.estado)}>
                      precio sin impuestos:{" "}
                      {formatPrice(producto.producto?.preciosinimp, signoMoneda)}
                    </Tag>
                    <Tag color="blue">
                      total con impuestos:{" "}
                      {formatPrice(
                        parseFloat(producto.producto?.preciosinimp || 0) *
                          producto.cantidad *
                          (1 +
                            parseFloat(producto.producto?.grupo?.impuesto || 0) /
                              100),
                        signoMoneda
                      )}
                    </Tag>
                  </div>
                </div>
              }
              style={{ marginBottom: 8 }}
            >
              <List
                dataSource={serialesPaginados}
                renderItem={(serial) => (
                  <List.Item>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span
                        style={{ fontFamily: "monospace", fontSize: "14px" }}
                      >
                        {serial.serial}
                      </span>
                      <Tag color={getEstadoColor(serial.estado)}>
                        {getEstadoText(serial.estado)}
                      </Tag>
                    </div>
                  </List.Item>
                )}
              />

              {paginacion && producto?.cantidad > paginacion.pageSize && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <Pagination
                    current={paginacion.current}
                    pageSize={paginacion.pageSize}
                    total={paginacion.total}
                    showSizeChanger={true}
                    showQuickJumper={true}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} de ${total} seriales`
                    }
                    pageSizeOptions={["5", "10", "20", "50"]}
                    onChange={(page, pageSize) =>
                      handlePaginacionChange(
                        producto?.producto?.id,
                        page,
                        pageSize
                      )
                    }
                    size="small"
                  />
                </div>
              )}
            </Panel>
          );
        })}
      </Collapse>

      {/* Productos No Serializados */}
      {productosNoSerializados.length > 0 && (
        <>
          <div style={{ marginTop: 24, marginBottom: 16 }}>
            <h4>
              Productos No Serializados ({productosNoSerializados.length})
            </h4>
          </div>

          <Collapse defaultActiveKey={[]} ghost>
            {productosNoSerializados.map((item, index) => (
              <Panel
                key={`ns-producto-${index}`}
                header={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>
                      {item.inventario_ns?.nombre || "Producto sin nombre"}
                    </span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Tag color="orange">
                        {item.cantidad} unidades
                      </Tag>
                      <Tag color={item.estado === "prestado" ? "blue" : "gold"}>
                        {item.estado || "Desconocido"}
                      </Tag>
                      <Tag color="green">
                        precio sin impuestos:{" "}
                        {formatPrice(item.inventario_ns?.producto?.preciosinimp, signoMoneda)}
                      </Tag>
                      <Tag color="blue">
                        total con impuestos:{" "}
                        {formatPrice(
                          parseFloat(item.inventario_ns?.producto?.preciosinimp || 0) *
                            parseFloat(item.cantidad || 0) *
                            (1 +
                              parseFloat(item.inventario_ns?.producto?.grupo?.impuesto || 0) /
                                100),
                          signoMoneda
                        )}
                      </Tag>
                    </div>
                  </div>
                }
                style={{ marginBottom: 8 }}
              >
                <div style={{ padding: "8px 0" }}>
                  <p>
                    <strong>Producto:</strong>{" "}
                    {item.inventario_ns?.producto?.nombre || "Desconocido"}
                  </p>
                  <p>
                    <strong>Cantidad:</strong>{" "}
                    {item.cantidad}
                  </p>
                  <p>
                    <strong>Estado:</strong>{" "}
                    {item.estado || "Desconocido"}
                  </p>
                  <p>
                    <strong>Precio unitario:</strong>{" "}
                    {formatPrice(item.inventario_ns?.producto?.preciosinimp, signoMoneda)}
                  </p>
                  <p>
                    <strong>Impuesto:</strong>{" "}
                    {item.inventario_ns?.producto?.grupo?.impuesto || 0}%
                  </p>
                </div>
              </Panel>
            ))}
          </Collapse>
        </>
      )}

      {/* Mensaje cuando no hay productos */}
      {productosAgrupados.length === 0 && productosNoSerializados.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No hay productos asociados a este préstamo"
          />
        )}
    </Modal>
  );
}

export default LoanDetailsModal;
